import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate, ViewSlot } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/Edit'

import { Paper } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId, GridValueFormatterParams } from '@mui/x-data-grid'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import GrupoScope from '../grupo.scopes'
import { GrupoRow } from '../grupo.types'

const LOG = Logger.get('GRUPO-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    syncButton: {
        alignSelf: 'center'
    }
})

type GrupoViewProps = {
    className?: string
    scope: GrupoScope
}
class GrupoViewClass implements FCClassContext<GrupoViewProps> {
    // :: emissors

    readonly emitOnFind = () => {
        this.scope.nomeGrupoInputRef = this.nomeGrupoInputRef.current?.value || ''
        this.scope.onFind().catch(LOG.caught)
    }

    readonly emitOnNew = () => {
        this.scope.onNew().catch(LOG.caught)
    }

    readonly handleEditClick = (id: GridRowId) => () => {
        this.scope.idSelected = id as number
        this.scope.onEdit().catch(LOG.caught)
    }

    readonly handleDeleteClick = (id: GridRowId) => () => {
        this.scope.idSelected = id as number
        this.scope.onDelete().catch(LOG.caught)
    }

    // :: Fields
    scope!: GrupoScope
    sytles!: ReturnType<typeof useStyles>
    grupoColumns = static_build_grupo_columns(this.handleEditClick, this.handleDeleteClick)
    nomeGrupoInputRef!: React.MutableRefObject<HTMLInputElement | undefined>

    // :: Methods

    onSyncState({ scope }: GrupoViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: GrupoViewProps) {
        this.scope = bindUpdate(React, scope)
        this.nomeGrupoInputRef = React.useRef<HTMLInputElement>()

        const classes = this.sytles.classes

        if (scope.waiting) {
            return <ViewSlot scope={scope.waiting} />
        }

        return (
            <div className={clsx(classes.view, className)}>
                <Box display="flex" flexDirection="row" alignItems="baseline" gap="10px">
                    <TextField
                        className={classes.cnpjInput}
                        inputRef={this.nomeGrupoInputRef}
                        label="Filtre pelo nome do grupo de análise"
                        variant="standard"
                    />
                    <Button variant="outlined" onClick={this.emitOnFind}>
                        Buscar
                    </Button>
                    <Button variant="outlined" onClick={this.emitOnNew}>
                        Novo
                    </Button>
                </Box>
                <Paper elevation={3} style={{ padding: 10 }}>
                    <DataGrid
                        rows={this.scope.grupos}
                        columns={this.grupoColumns}
                        getRowId={static_getGrupoRowId}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10
                                }
                            }
                        }}
                    />
                </Paper>
            </div>
        )
    }
}

export const GrupoView = classToFComponent(GrupoViewClass, React)
export default GrupoView

function static_build_grupo_columns(
    handleEditClick: (id: GridRowId) => () => void,
    handleDeleteClick: (id: GridRowId) => () => void
): GridColDef[] {
    return [
        {
            field: 'tipo',
            headerName: 'Tipo',
            width: 100,
            valueFormatter: (params: GridValueFormatterParams<string>) => {
                return params.value === 'BORDERO' ? 'Bordero' : params.value
            }
        },
        {
            field: 'nome',
            headerName: 'Nome',
            width: 300
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 100,
            valueFormatter: (params: GridValueFormatterParams<string>) => {
                return params.value === 'ATIVO' ? 'Ativo' : 'Inativo'
            }
        },
        {
            field: 'rating1Start',
            headerName: 'Rating 1 Start',
            width: 100
        },
        {
            field: 'rating2Start',
            headerName: 'Rating 2 Start',
            width: 100
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        key={'edit' + id}
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        key={'del' + id}
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />
                ]
            }
        }
    ]
}

function static_getGrupoRowId(row: GrupoRow) {
    return row.id + ''
}
